<template>
  <div class="ga-admin-page">
    <AdminHeader
      :title="$t('ga_admin_overview_title')"
      :subtitle="$t('ga_admin_overview_subtitle')"
      compact
    />

    <div class="page-body">
      <div v-if="loading" class="loading-card">
        <div class="loading-progress-head">
          <div class="loading-progress-step">
            <span>{{ $t('ga_admin_loading_step', {step: loadingProgress.currentLabel || $t('ga_admin_loading_overview')}) }}</span>
            <small>{{ $t('ga_admin_loading_position', {current: loadingCurrentModuleIndex, total: loadingProgress.totalModules}) }}</small>
          </div>
          <div class="loading-progress-percent">{{ loadingProgress.percent }}%</div>
        </div>
        <div class="loading-progress-track">
          <div class="loading-progress-fill" :style="{width: `${loadingProgress.percent}%`}"></div>
        </div>
      </div>
      <div v-else-if="errorMessage" class="state-card warning">{{ errorMessage }}</div>
      <template v-else>

        <section class="section-card">
          <div class="section-head">
            <div>
              <div class="section-title">{{ $t('ga_admin_section_overall') }}</div>
              <div class="section-desc">{{ $t('ga_admin_section_overall_desc') }}</div>
            </div>
          </div>
          <div class="stats-grid">
            <div v-for="item in overviewStats" :key="item.title" class="stat-card">
              <div class="stat-title">{{ item.title }}</div>
              <div class="stat-value">{{ item.value }}</div>
              <div :class="['stat-change', item.tone]">{{ item.change }}</div>
              <div class="stat-desc">{{ item.desc }}</div>
            </div>
          </div>
        </section>

        <section v-if="expertFeedbackByLevel.levels.length" class="section-card expert-level-section">
          <div class="section-head">
            <div>
              <div class="section-title">{{ $t('ga_admin_expert_level_feedback_title') }}</div>
              <div class="section-desc">{{ $t('ga_admin_expert_level_feedback_desc') }}</div>
            </div>
          </div>
          <div class="expert-overall-grid">
            <div class="expert-overall-item primary">
              <span>{{ $t('ga_admin_expert_familiarity') }}</span>
              <strong>{{ formatRate(expertFeedbackByLevel.overall.familiarityPositiveRate) }}</strong>
              <small>{{ $t('ga_admin_expert_high_score_rate') }}</small>
            </div>
            <div class="expert-overall-item">
              <span>{{ $t('ga_admin_expert_alignment') }}</span>
              <strong>{{ formatRate(expertFeedbackByLevel.overall.alignmentPositiveRate) }}</strong>
              <small>{{ $t('ga_admin_expert_high_score_rate') }}</small>
            </div>
            <div class="expert-overall-item">
              <span>{{ $t('ga_admin_expert_feedback_coverage') }}</span>
              <strong>{{ expertFeedbackByLevel.overall.feedbackCustomerCount }}/{{ expertFeedbackByLevel.overall.customerCount }}</strong>
            </div>
          </div>

          <div class="expert-level-list">
            <article v-for="level in expertFeedbackByLevel.levels" :key="level.level" class="expert-level-card">
              <div class="expert-level-head">
                <div>
                  <strong>{{ level.level }}</strong>
                  <span>{{ $t('ga_admin_expert_level_customer_meta', {
                    customers: level.customerCount,
                    feedbackCustomers: level.feedbackCustomerCount,
                  }) }}</span>
                </div>
              </div>

              <div class="level-metric-block">
                <div class="level-metric-head">
                  <span>{{ $t('ga_admin_expert_familiarity') }}</span>
                  <span class="level-metric-values">
                    <strong>{{ formatRate(level.familiarityPositiveRate) }}</strong>
                    <small>{{ $t('ga_admin_expert_high_score_rate') }}</small>
                  </span>
                </div>
                <div v-if="level.customerCount" class="score-track">
                  <i
                    class="score-unanswered"
                    :style="scoreSegmentStyle(
                      level.familiarityDistribution,
                      'unanswered',
                      level.familiarityMissingCount,
                    )"
                  ></i>
                  <i
                    v-for="score in scoreLevels"
                    :key="`familiarity-${level.level}-${score}`"
                    :class="`score-${score}`"
                    :style="scoreSegmentStyle(
                      level.familiarityDistribution,
                      score,
                      level.familiarityMissingCount,
                    )"
                  ></i>
                </div>
                <div v-else class="score-track-empty">{{ $t('ga_admin_expert_level_no_feedback') }}</div>
                <div class="score-legend">
                  <span>
                    <i class="score-unanswered"></i>{{ $t('ga_admin_expert_unanswered_distribution_item', {
                      count: level.familiarityMissingCount,
                    }) }}
                  </span>
                  <span v-for="score in scoreLevels" :key="`familiarity-legend-${level.level}-${score}`">
                    <i :class="`score-${score}`"></i>{{ $t('ga_admin_expert_score_distribution_item', {
                      score,
                      count: level.familiarityDistribution[score],
                    }) }}
                  </span>
                </div>
              </div>

              <div class="level-metric-block">
                <div class="level-metric-head">
                  <span>{{ $t('ga_admin_expert_alignment') }}</span>
                  <span class="level-metric-values">
                    <strong>{{ formatRate(level.alignmentPositiveRate) }}</strong>
                    <small>{{ $t('ga_admin_expert_high_score_rate') }}</small>
                  </span>
                </div>
                <div v-if="level.customerCount" class="score-track">
                  <i
                    class="score-unanswered"
                    :style="scoreSegmentStyle(
                      level.alignmentDistribution,
                      'unanswered',
                      level.alignmentMissingCount,
                    )"
                  ></i>
                  <i
                    v-for="score in scoreLevels"
                    :key="`alignment-${level.level}-${score}`"
                    :class="`score-${score}`"
                    :style="scoreSegmentStyle(
                      level.alignmentDistribution,
                      score,
                      level.alignmentMissingCount,
                    )"
                  ></i>
                </div>
                <div v-else class="score-track-empty">{{ $t('ga_admin_expert_level_no_feedback') }}</div>
                <div class="score-legend">
                  <span>
                    <i class="score-unanswered"></i>{{ $t('ga_admin_expert_unanswered_distribution_item', {
                      count: level.alignmentMissingCount,
                    }) }}
                  </span>
                  <span v-for="score in scoreLevels" :key="`alignment-legend-${level.level}-${score}`">
                    <i :class="`score-${score}`"></i>{{ $t('ga_admin_expert_score_distribution_item', {
                      score,
                      count: level.alignmentDistribution[score],
                    }) }}
                  </span>
                </div>
              </div>
            </article>
          </div>
          <div class="expert-level-note">{{ $t('ga_admin_expert_level_score_note') }}</div>
        </section>

        <section class="section-card">
          <div class="section-head">
            <div>
              <div class="section-title">{{ $t('ga_admin_section_ai_daily') }}</div>
              <div class="section-desc">{{ $t('ga_admin_section_ai_daily_desc') }}</div>
            </div>
            <button class="ghost-btn ai-action-btn" @click="openAi($t('ga_admin_ai_prompt_daily'))">
              <font-awesome-icon :icon="['far', 'lightbulb']" class="ai-btn-icon" />
              <span>{{ $t('ga_admin_further_analysis') }}</span>
            </button>
          </div>
          <div class="summary-list">
            <div v-for="item in aiHighlights" :key="item" class="summary-item">
              <span class="summary-dot"></span>
              <span>{{ item }}</span>
            </div>
          </div>
        </section>

        <section class="section-card">
          <div class="section-head">
            <div>
              <div class="section-title">{{ $t('ga_admin_section_feedback_overview') }}</div>
              <div class="section-desc">{{ $t('ga_admin_section_feedback_overview_desc') }}</div>
            </div>
          </div>
          <div class="board-grid">
            <div v-for="board in mindsetBoards" :key="board.title" class="board-card">
              <div class="board-title">{{ board.title }}</div>
              <div class="board-subtitle">{{ board.subtitle }}</div>
              <div class="board-summary">{{ board.summary }}</div>
              <div class="metric-row">
                <div v-for="metric in board.metrics" :key="metric.label" class="metric-chip">
                  <span class="metric-label">{{ metric.label }}</span>
                  <span class="metric-value">{{ metric.value }}</span>
                </div>
              </div>
              <div class="tag-row">
                <span v-for="tag in board.tags" :key="tag" class="soft-tag">{{ tag }}</span>
                <span v-if="!board.tags.length" class="soft-tag empty">{{ $t('ga_admin_empty_feedback_tags') }}</span>
              </div>
            </div>
          </div>
          <div class="rule-note">
            <div v-for="rule in positiveFeedbackRules" :key="rule" class="rule-row">{{ rule }}</div>
          </div>
        </section>
      </template>

    </div>
  </div>
</template>

<script>
import AdminHeader from './components/AdminHeader.vue';
import {loadGaAdminAnalytics} from './analytics';

export default {
  name: 'GAAdminOverview',
  components: {
    AdminHeader,
  },
  data() {
    return {
      loading: false,
      loadingProgress: {
        completedModules: 0,
        currentLabel: '',
        percent: 0,
        totalModules: 4,
      },
      errorMessage: '',
      analytics: {
        overviewStats: [],
        aiHighlights: [],
        mindsetBoards: [],
        positiveFeedbackRules: [],
        expertFeedbackByLevel: {
          overall: {
            customerCount: 0,
            feedbackCustomerCount: 0,
            familiarityMissingCount: 0,
            familiarityPositiveRate: null,
            alignmentMissingCount: 0,
            alignmentPositiveRate: null,
          },
          levels: [],
        },
      },
    };
  },
  computed: {
    overviewStats() {
      return this.analytics.overviewStats;
    },
    aiHighlights() {
      return this.analytics.aiHighlights;
    },
    mindsetBoards() {
      return this.analytics.mindsetBoards;
    },
    positiveFeedbackRules() {
      return this.analytics.positiveFeedbackRules;
    },
    expertFeedbackByLevel() {
      return this.analytics.expertFeedbackByLevel;
    },
    scoreLevels() {
      return [0, 1, 2, 3];
    },
    loadingCurrentModuleIndex() {
      const total = Math.max(Number(this.loadingProgress.totalModules) || 1, 1);
      return Math.min((Number(this.loadingProgress.completedModules) || 0) + 1, total);
    },
  },
  watch: {
    '$i18n.locale'() {
      this.fetchAnalytics();
    },
  },
  created() {
    this.fetchAnalytics();
  },
  methods: {
    async fetchAnalytics() {
      this.loading = true;
      this.errorMessage = '';
      this.resetLoadingProgress(this.$t('ga_admin_loading_overview_source'));
      try {
        const analytics = await loadGaAdminAnalytics({
          onProgress: this.updateLoadingProgress,
        });
        this.analytics = {
          ...this.analytics,
          ...analytics,
        };
        this.updateLoadingProgress({
          completedModules: this.loadingProgress.totalModules,
          currentLabel: this.$t('ga_admin_loading_overview_dashboard'),
          percent: 100,
          totalModules: this.loadingProgress.totalModules,
        });
      } catch (e) {
        console.error(e);
        this.errorMessage = this.$t('ga_admin_error_overview');
      } finally {
        this.loading = false;
      }
    },
    resetLoadingProgress(currentLabel) {
      this.loadingProgress = {
        completedModules: 0,
        currentLabel,
        percent: 0,
        totalModules: 4,
      };
    },
    updateLoadingProgress({
      completedModules = 0,
      currentLabel = '',
      labelKey = '',
      percent,
      totalModules = 4,
    } = {}) {
      const total = Math.max(Number(totalModules) || 1, 1);
      const completed = Math.min(Number(completedModules) || 0, total);
      this.loadingProgress = {
        completedModules: completed,
        currentLabel: currentLabel || (labelKey ? this.$t(labelKey) : this.loadingProgress.currentLabel),
        percent: percent === undefined ? Math.min(99, Math.round((completed / total) * 100)) : percent,
        totalModules: total,
      };
    },
    openAi(message) {
      this.$root.$emit('open-ai-chat', message, true, 'SRCopilotInsightGBA');
    },
    formatRate(value) {
      return value === null || value === undefined ? '--' : `${value}%`;
    },
    scoreSegmentStyle(distribution, score, unansweredCount = 0) {
      const unanswered = Number(unansweredCount || 0);
      const total = (distribution || []).reduce((sum, count) => sum + Number(count || 0), unanswered);
      const count = score === 'unanswered' ? unanswered : Number(distribution?.[score] || 0);
      return {
        width: total ? `${(count / total) * 100}%` : '0',
        display: count ? 'block' : 'none',
      };
    },
  },
};
</script>

<style scoped lang="scss">
.ga-admin-page {
  min-height: 100%;
  background-color: #f2f5f9;
  background: linear-gradient(175deg, #e6f0ff 10%, #f0e9f4 30%, #f2f5f9 50%);
}

.page-body {
  padding: 12px 12px 90px;
}

.state-card {
  margin-bottom: 12px;
  padding: 12px 14px;
  border-radius: 12px;
  background: #fff;
  color: #666;
  font-size: 13px;
  line-height: 1.5;
}

.state-card.warning {
  color: #aa341b;
}

.loading-card {
  min-height: 240px;
  display: flex;
  width: min(260px, 76vw);
  margin: 0 auto;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.loading-progress-head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
}

.loading-progress-step {
  min-width: 0;
  color: #596579;
  font-size: 11px;
  line-height: 16px;
  text-align: left;
}

.loading-progress-step span {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.loading-progress-step small {
  display: block;
  margin-top: 1px;
  color: #8b95a5;
  font-size: 10px;
  line-height: 14px;
}

.loading-progress-percent {
  flex-shrink: 0;
  color: #aa341b;
  font-size: 15px;
  font-weight: 600;
  line-height: 18px;
}

.loading-progress-track {
  width: 100%;
  height: 5px;
  margin-top: 8px;
  overflow: hidden;
  border-radius: 999px;
  background: #f0e2dd;
}

.loading-progress-fill {
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #aa341b 0%, #d77a48 100%);
  transition: width 0.25s ease;
}

.stats-grid,
.board-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.section-card,
.stat-card,
.board-card {
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.section-card {
  margin-bottom: 12px;
  padding: 15px;
}

.section-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.section-title {
  font-size: 17px;
  font-weight: 700;
  color: #333;
}

.section-desc {
  margin-top: 4px;
  font-size: 12px;
  line-height: 1.5;
  color: #999;
}

.ghost-btn {
  flex-shrink: 0;
  border: 1.5px solid #aa341b;
  border-radius: 999px;
  padding: 8px 12px;
  background: #fff;
  color: #aa341b;
  font-size: 12px;
}

.ai-action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: 0;
  background: linear-gradient(135deg, #4d50e9 0%, #b84fd7 48%, #aa341b 100%);
  color: #fff;
  box-shadow: 0 6px 14px rgba(170, 52, 27, 0.2);
}

.ai-btn-icon {
  font-size: 13px;
}

.stat-card {
  padding: 14px;
  background: linear-gradient(180deg, #fff, #f8f6f5);
}

.stat-title {
  font-size: 12px;
  color: #666;
}

.stat-value {
  margin-top: 8px;
  font-size: 28px;
  font-weight: 700;
  color: #333;
}

.stat-change {
  margin-top: 8px;
  font-size: 12px;
  font-weight: 600;
}

.stat-change.positive {
  color: #0e9f6e;
}

.stat-change.neutral {
  color: #1f3c88;
}

.stat-change.warning {
  color: #aa341b;
}

.stat-desc {
  margin-top: 8px;
  font-size: 12px;
  line-height: 1.5;
  color: #999;
}

.expert-level-section {
  border: 1px solid rgba(170, 52, 27, 0.12);
}

.expert-overall-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
}

.expert-overall-item {
  min-width: 0;
  padding: 8px 6px;
  border-radius: 12px;
  background: #f7f9fc;
  text-align: center;
}

.expert-overall-item.primary {
  background: #fff2ee;
}

.expert-overall-item span,
.expert-overall-item strong,
.expert-overall-item small {
  display: block;
}

.expert-overall-item span {
  color: #7d8490;
  font-size: 10px;
  line-height: 15px;
}

.expert-overall-item strong {
  margin-top: 1px;
  color: #26384a;
  font-size: 21px;
  line-height: 26px;
}

.expert-overall-item.primary strong {
  color: #aa341b;
}

.expert-overall-item small {
  color: #9a9ea5;
  font-size: 9px;
  line-height: 13px;
}

.expert-level-list {
  display: grid;
  gap: 10px;
  margin-top: 12px;
}

.expert-level-card {
  padding: 12px;
  border: 1px solid #edf0f3;
  border-radius: 14px;
  background: #fbfcfd;
}

.expert-level-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
}

.expert-level-head > div {
  min-width: 0;
}

.expert-level-head strong,
.expert-level-head span {
  display: block;
}

.expert-level-head strong {
  color: #26384a;
  font-size: 15px;
  line-height: 20px;
}

.expert-level-head > div > span {
  margin-top: 2px;
  color: #9a9ea5;
  font-size: 10px;
  line-height: 15px;
}

.level-metric-block {
  margin-top: 12px;
}

.level-metric-block + .level-metric-block {
  padding-top: 11px;
  border-top: 1px solid #edf0f3;
}

.level-metric-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
  color: #5d6570;
  font-size: 12px;
}

.level-metric-head strong {
  color: #aa341b;
  font-size: 15px;
}

.level-metric-values {
  text-align: right;
}

.level-metric-values strong,
.level-metric-values small {
  display: block;
}

.level-metric-values small {
  margin-top: 1px;
  color: #9a9ea5;
  font-size: 9px;
  font-weight: 400;
  line-height: 12px;
}

.score-track {
  display: flex;
  width: 100%;
  height: 8px;
  margin-top: 7px;
  overflow: hidden;
  border-radius: 999px;
  background: #edf0f3;
}

.score-track > i {
  height: 100%;
}

.score-track-empty {
  margin-top: 7px;
  padding: 5px 8px;
  border-radius: 7px;
  background: #f1f3f5;
  color: #a3a7ad;
  font-size: 10px;
  text-align: center;
}

.score-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 5px 12px;
  margin-top: 7px;
  color: #8a8f99;
  font-size: 10px;
}

.score-legend span {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.score-legend i {
  width: 7px;
  height: 7px;
  border-radius: 50%;
}

.score-0 {
  background: #d46b60;
}

.score-unanswered {
  background: #cfd4da;
}

.score-1 {
  background: #e6aa63;
}

.score-2 {
  background: #7598c7;
}

.score-3 {
  background: #54a082;
}

.expert-level-note {
  margin-top: 10px;
  color: #999;
  font-size: 10px;
  line-height: 1.5;
}

.summary-list {
  display: grid;
  gap: 10px;
}

.summary-item {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 12px 14px;
  border-radius: 14px;
  background: #fff;
  border: 1px solid #f2f5f9;
  font-size: 13px;
  line-height: 1.6;
  color: #333;
}

.summary-dot {
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  margin-top: 7px;
  border-radius: 50%;
  background: #aa341b;
}

.board-card {
  padding: 14px;
  background: #fff;
}

.hero-grid {
  margin-bottom: 12px;
}

.board-title {
  font-size: 15px;
  font-weight: 700;
  color: #333;
}

.board-subtitle {
  margin-top: 4px;
  font-size: 11px;
  color: #999;
}

.board-summary {
  margin-top: 10px;
  font-size: 12px;
  line-height: 1.6;
  color: #666;
}

.metric-row,
.tag-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
}

.metric-chip {
  min-width: 88px;
  padding: 10px;
  border-radius: 12px;
  background: #f2f5f9;
}

.metric-label {
  display: block;
  font-size: 11px;
  color: #999;
}

.metric-value {
  display: block;
  margin-top: 6px;
  font-size: 16px;
  font-weight: 700;
  color: #333;
}

.soft-tag {
  padding: 6px 10px;
  border-radius: 999px;
  background: #fceceb;
  font-size: 11px;
  color: #aa341b;
}

.soft-tag.empty {
  background: #f8f6f5;
  color: #999;
}

.rule-note {
  margin-top: 12px;
  padding: 12px;
  border-radius: 12px;
  background: #f8f6f5;
}

.rule-row {
  font-size: 11px;
  line-height: 1.6;
  color: #7a4d42;
}

.rule-row + .rule-row {
  margin-top: 6px;
}

@media (max-width: 360px) {
  .stats-grid,
  .board-grid {
    grid-template-columns: 1fr;
  }
}
</style>
